import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormItem, FormLabel } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Widget } from './Widget';

export function NewValueWidgetData ({ data, onChangeField, onCompleted }) {
    const
        [site, setSite] = useState([]),
        [indicatorOptions, setIndicatorOptions] = useState([]);

    const
        decimalOptions = ['0', '1', '2', '3'],
        periodOptions = [
            { label: i18next.t('glossary.last_value').capitalize(), value: 'last_value' },
            { label: i18next.t('periods.verbose.wtd').capitalize(), value: 'wtd' },
            { label: i18next.t('periods.verbose.mtd').capitalize(), value: 'mtd' },
            { label: i18next.t('periods.verbose.ytd').capitalize(), value: 'ytd' }
        ],
        unitScaleOptions = [
            { label: i18next.t('unit_scales.auto'), value: -1 },
            { label: i18next.t('unit_scales.full'), value: 0 },
            { label: i18next.t('unit_scales.thousands'), value: 3 },
            { label: i18next.t('unit_scales.millions'), value: 6 },
            { label: i18next.t('unit_scales.billions'), value: 9 }
        ],
        sitesOptions = WiseMetering.zones.activeSites().map(model => ({ id: model.id, name: model.get('name') }));

    const formSchema = z.object({
        decimals: z.string().min(1, i18next.t('form_validations.generic.required')),
        indicatorId: z.string().min(1, i18next.t('form_validations.generic.indicator_required')),
        period: z.string().min(1, i18next.t('form_validations.generic.required')),
        unitScale: z.number().int(i18next.t('form_validations.generic.required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            decimals: data.data?.decimals || '2',
            indicatorId: data.data?.indicatorId || '',
            period: data.data?.period || '',
            unitScale: data.data?.unitScale || -1
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = useRef({
        decimals: debounce(() => form.trigger('decimals'), 600),
        indicatorId: debounce(() => form.trigger('indicatorId'), 600),
        period: debounce(() => form.trigger('period'), 600),
        unitScale: debounce(() => form.trigger('unitScale'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    useEffect(() => {
        setIndicatorOptions(
            WiseMetering.indicators
                .filter(model => model.get('site_id') === site)
                .map(model => ({ id: model.id, name: model.get('name') }))
        );
        form.setValue('indicatorId', ''); // Reset selected indicator when site changes
    }, [site]);

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <FormField
                        control={form.control}
                        name="sites"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.site').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            setSite(val);

                                            const selected = sitesOptions.find(i => i.id === val);

                                            onChangeField('subtitle')(selected?.name || '');

                                            debouncedTriggers.current.indicatorId();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('sites.select_site')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {sitesOptions.map(site => (
                                                <SelectItem key={site.id} value={site.id}>
                                                    {site.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="indicatorId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.indicator').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            const selected = indicatorOptions.find(i => i.id === val);

                                            onChangeField('data')({
                                                ...data.data,
                                                indicatorId: val
                                            });

                                            onChangeField('title')(selected?.name || '');

                                            debouncedTriggers.current.indicatorId();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('indicators.select_indicator').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {indicatorOptions.map(indicator => (
                                                <SelectItem key={indicator.id} value={indicator.id}>
                                                    {indicator.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="period"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('common.reference_period').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            const selected = periodOptions.find(p => p.value === val);

                                            onChangeField('data')({
                                                ...data.data,
                                                period: val
                                            });

                                            onChangeField('legend')(selected?.label || '');

                                            debouncedTriggers.current.unitScale();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('common.reference_period').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            { periodOptions.map(period => (
                                                <SelectItem key={period.value} value={period.value}>
                                                    {period.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="unitScale"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('indicatorKinds.power_factor').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            onChangeField('data')({
                                                ...data.data,
                                                unitScale: val
                                            });

                                            debouncedTriggers.current.unitScale();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('indicatorKinds.power_factor').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            { unitScaleOptions.map(unitScale => (
                                                <SelectItem key={unitScale.value} value={unitScale.value}>
                                                    {unitScale.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="decimals"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.decimals').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            onChangeField('data')({
                                                ...data.data,
                                                decimals: val
                                            });

                                            debouncedTriggers.current.decimals();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('glossary.decimals').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            { decimalOptions.map(decimal => (
                                                <SelectItem key={decimal} value={decimal}>
                                                    {decimal}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
